import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusExerciseAnswer } from '@chancenwerk/campus-learning-content/api/validateExercises.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-image.js';
import { type ChromiumInput } from '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-writer/cr-writer.js';
import '@chancenwerk/design/components/fonts.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, of, switchMap } from 'rxjs';
import { initKatexComponent } from '../../initKatexComponent.js';

@customElement('campus-component-exercise-text')
export class CampusComponentExerciseText extends ChemistryLitElement {
  @property({ attribute: false }) public exerciseID = new RXController(this, '');
  @property({ attribute: false }) public lessonID = new RXController(this, '');
  @property({ attribute: false }) public mode = new RXController(this, 'play');

  @property({ attribute: false }) public exercisePreSave = new RXController(this, new CampusExercise({}));

  @state() private readonly _exercise = new RXController(
    this,
    combineLatest([this.exerciseID.observable, this.exercisePreSave.observable]).pipe(
      switchMap(([liveExerciseId, presaveExercise]) => {
        if (liveExerciseId)
          return CampusExercise.subscribeByID<CampusExercise>(liveExerciseId)(CampusExercise.allFields);
        return of(presaveExercise);
      }),
    ),
  );

  @state() private readonly _disableSubmit = new RXController<boolean>(this, false);
  @state() private readonly _answer = new RXController<string>(this, '');
  @state() private readonly _showSolution = new RXController<boolean>(this, false);
  @state() private readonly _showSolutionOption = new RXController<boolean>(this, false);

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  private readonly _componentRef: Ref = createRef();

  override connectedCallback() {
    initKatexComponent();
    super.connectedCallback();
  }

  override render() {
    if (!this._exercise.value) {
      return html`
        <cr-flex space="md" center crossaxis-center>
          <cr-campus-loader></cr-campus-loader>
        </cr-flex>
      `;
    }
    return html`
      <cr-card ${ref(this._componentRef)} ${css({ width: '100%' })}>
        <cr-flex space="sm">
          <cr-text>${unsafeHTML(this._getQuestionWithEmbeddedFormula())}</cr-text>
          ${this._showSolution.value
            ? html` <cr-text>
                  Musterlösung: <br />
                  <br />
                  ${unsafeHTML(this._getCleanedSampleSolution())}
                </cr-text>
                <cr-text>${unsafeHTML(this._exercise.value.questionAnswer)}</cr-text>`
            : nothing}
          ${this._getVariableAnswerInput()} ${this._getFreeAnswerInput()}
          ${(this._showSolutionOption.value || this._exercise.value.questionAnswerVariableFields) &&
          this._exercise.value?.minAnswerLength >= 0
            ? html`
                <cr-button
                  @click=${() =>
                    this._showSolution.value ? this._showSolution.next(false) : this._showSolution.next(true)}
                >
                  Musterlösung ${this._showSolution.value ? html`verbergen` : html`anzeigen`}
                </cr-button>
              `
            : nothing}

          <cr-flex center crossaxis-center margin="md" space="md"> ${this._getSubmitButtonConditional()} </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getSubmitButtonConditional() {
    if (this.mode.value === 'preview') return nothing;

    if (this._disableSubmit.value) return this._getDisabledSubmitButton();

    if (
      this._exercise.value &&
      this._exercise.value.minAnswerLength >= 0 &&
      this._answer.value.length < this._exercise.value.minAnswerLength
    )
      return this._getDisabledSubmitButton();

    if (this._exercise.value && this._exercise.value.minAnswerLength >= 0) return this._getSubmitButton();

    if (this._exercise.value?.minAnswerLength === -1) return nothing;
    if (this._exercise.value?.questionAnswerVariableFields) return this._getSubmitButton();
    return nothing;
  }

  private _getSubmitButton() {
    return html` <cr-button @click=${async () => this._submitAnswer()}> Abgeben </cr-button> `;
  }

  private _getDisabledSubmitButton() {
    return html`<cr-button disabled> Abgeben </cr-button>`;
  }

  private _getFreeAnswerInput() {
    if (this._exercise.value && this._exercise.value.minAnswerLength >= 0) {
      return html`
        <cr-flex ${css({ paddingTop: '1rem' })} space="sm">
          <cr-text>Deine Antwort muss mindestens ${this._exercise.value.minAnswerLength} Zeichen lang sein.</cr-text>
          <cr-writer
            ${acss({
              'cr-flex': { background: 'none !important' },
            })}
            minheight="10vh"
            placeholder="Antwort"
            @value-changed=${(e: CustomEvent) => {
              this._answer.next(e.detail.value);
            }}
          ></cr-writer>
        </cr-flex>
      `;
    }
  }

  private _getVariableAnswerInput() {
    if (this._exercise.value?.questionAnswerVariableFields) {
      return html`
        <cr-text>
          <cr-flex horizontal space="md" crossaxis-center> ${this._getAnswerWithEmbeddedBlanks()} </cr-flex>
        </cr-text>
      `;
    }
  }

  private async _submitAnswer() {
    if (this.mode.value === 'preview') return;
    this._showSolutionOption.next(true);

    const answer = new CampusExerciseAnswer({
      textAnswer: this._answer.value,
      mathAnswers: this._getAllAnswerValuesAsArray() ?? [],
    });

    await LearningContentAPI.submitTextExercise(
      this._currentUser.value?._id || '',
      this.exerciseID.value,
      this.lessonID.value || '',
      answer,
    )();
    this._disableSubmit.next(true);
    void ChromiumSnackbar.show('Aufgabe abgegeben');
  }

  private _getAnswerWithEmbeddedBlanks() {
    const variableAnswers = this._exercise.value?.questionAnswerVariableFields
      .replaceAll(' ', '')
      .replaceAll('<p>', '')
      .replaceAll('</p>', '')
      .replaceAll(/<br ?\/?>/g, '')
      .split(',');

    if (variableAnswers?.map(a => a.replace(/\n/g, '')).filter(a => a.length > 0).length === 0) {
      return nothing;
    }

    return html`
      <cr-flex space="sm" center crossaxis-center w100>
        ${repeat(variableAnswers ?? [], answerString => {
          const splitAnswer = answerString.split('=');
          const parsedHTMLInput = splitAnswer[1]?.replace(
            CampusExercise.latexExpressionRegex,
            `<cr-input style="flex: 1" placeholder-mode="hidden"></cr-input>`,
          );
          return html` <cr-flex horizontal center crossaxis-center space="sm" w100>
            <cr-text>${splitAnswer[0]} =</cr-text>${unsafeHTML(parsedHTMLInput)}
          </cr-flex>`;
        })}
      </cr-flex>
    `;
  }

  private _getQuestionWithEmbeddedFormula() {
    let questionHTML = `${this._exercise.value?.questionText.replace(
      CampusExercise.latexExpressionRegex,
      `<katex-expression expression="$1"></katex-expression>`,
    )}`;

    questionHTML = questionHTML.replace(/(\.\.\/)+/, '/');
    return questionHTML;
  }

  private _getCleanedSampleSolution() {
    if (!this._exercise.value?.questionAnswerVariableFields) {
      return nothing;
    }
    return `${this._exercise.value.questionAnswerVariableFields.replace(CampusExercise.latexExpressionRegex, `$1`)}`;
  }

  private _getAllAnswerValuesAsArray() {
    if (!this._componentRef.value) {
      console.error('Something went wrong with the componentref');
      return;
    }
    return ([...this._componentRef.value.querySelectorAll('cr-input')] as ChromiumInput[]).map(
      (blank, index) => blank.value.value?.toString() || 'EMPTY_ANSWER',
    );
  }
}
